import { useEffect, useRef, useState } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';

/**
 * Arquivos de anexo nunca são servidos por URL pública: passam pelo endpoint
 * autenticado. Como <img src> não envia o header Authorization, o arquivo é
 * baixado como Blob e exibido por object URL.
 *
 * O cache é em memória e vive enquanto a página estiver aberta (a página do
 * atendimento recarrega os dados várias vezes; sem cache, as miniaturas
 * piscariam a cada recarga). Os object URLs são liberados em
 * clearAttachmentFileCache, chamada após excluir/editar um anexo.
 */

const cache = new Map();   // `${id}:${size}` -> { url, blob }
const pending = new Map(); // `${id}:${size}` -> Promise<{ url, blob }>

const buildKey = (id, size) => `${id}:${size}`;

const releaseKey = (key) => {
    const entry = cache.get(key);

    if (entry && entry.url) {
        URL.revokeObjectURL(entry.url);
    }

    cache.delete(key);
    pending.delete(key);
};

/**
 * Descarta o cache de um anexo (ou de todos, sem id).
 */
export const clearAttachmentFileCache = (id) => {
    if (id === undefined || id === null) {
        Array.from(cache.keys()).forEach(releaseKey);
        pending.clear();
        return;
    }

    Array.from(cache.keys())
        .filter(key => key.split(':')[0] === String(id))
        .forEach(releaseKey);

    Array.from(pending.keys())
        .filter(key => key.split(':')[0] === String(id))
        .forEach(key => pending.delete(key));
};

/**
 * Baixa (ou reaproveita) o arquivo do anexo. `loader` é o
 * GetPatientAttachmentFile do DataProvider.
 */
export const loadAttachmentFile = (loader, id, size = 'full') => {
    const key = buildKey(id, size);

    if (cache.has(key)) {
        return Promise.resolve(cache.get(key));
    }

    if (pending.has(key)) {
        return pending.get(key);
    }

    const request = loader(id, size)
        .then((blob) => {
            const entry = { url: URL.createObjectURL(blob), blob: blob };
            cache.set(key, entry);
            pending.delete(key);
            return entry;
        })
        .catch((error) => {
            pending.delete(key);
            throw error;
        });

    pending.set(key, request);

    return request;
};

/**
 * Entrada em cache de um anexo, ou null se ainda não foi baixado.
 * @param {number|string} id id do anexo
 * @param {'thumb'|'full'} size versão desejada
 * @returns {{url: string, blob: Blob}|null}
 */
export const getCachedAttachmentFile = (id, size = 'full') => {
    const key = buildKey(id, size);

    return cache.has(key) ? cache.get(key) : null;
};

/**
 * Hook com a object URL do anexo.
 *
 * Lê o cache de forma síncrona (no estado inicial e no efeito) para que um
 * anexo já baixado apareça direto, sem o frame de shimmer: a página do
 * atendimento remonta as miniaturas várias vezes (recarga de dados, troca de
 * linha por reordenação, etc.) e, sem essa leitura síncrona, cada remontagem
 * piscaria mesmo com o arquivo já em memória.
 *
 * @param {number|string} id id do anexo
 * @param {'thumb'|'full'} size versão desejada
 * @param {boolean} enabled quando false, não baixa nada
 * @returns {{url: string|null, blob: Blob|null, loading: boolean, error: string|null}}
 */
export const useAuthFileUrl = (id, size = 'full', enabled = true) => {
    const { GetPatientAttachmentFile } = DataProviderInstance();
    // Key (id:size) a que o `url` do estado atual pertence, para nunca reaproveitar
    // o estado de um anexo diferente (ex.: navegação entre fotos no visualizador,
    // que troca `id` sem remontar o componente).
    const stateKeyRef = useRef(null);

    const [state, setState] = useState(() => {
        const cached = enabled && id ? getCachedAttachmentFile(id, size) : null;

        if (cached) {
            stateKeyRef.current = buildKey(id, size);
            return { url: cached.url, blob: cached.blob, loading: false, error: null };
        }

        return { url: null, blob: null, loading: false, error: null };
    });

    useEffect(() => {
        if (!enabled || !id) {
            stateKeyRef.current = null;
            setState({ url: null, blob: null, loading: false, error: null });
            return undefined;
        }

        const key = buildKey(id, size);
        const cached = getCachedAttachmentFile(id, size);

        if (cached) {
            stateKeyRef.current = key;
            setState({ url: cached.url, blob: cached.blob, loading: false, error: null });
            return undefined;
        }

        let active = true;

        // Evita o flash quando o efeito roda de novo com as mesmas deps (ex.:
        // dupla invocação do StrictMode): só reaproveita o estado atual se ele já
        // pertence a este mesmo id+size (nunca ao trocar de anexo).
        setState((previous) => (stateKeyRef.current === key && previous.url)
            ? previous
            : { url: null, blob: null, loading: true, error: null });

        loadAttachmentFile(GetPatientAttachmentFile, id, size)
            .then((entry) => {
                if (!active) return;
                stateKeyRef.current = key;
                setState({ url: entry.url, blob: entry.blob, loading: false, error: null });
            })
            .catch((error) => {
                if (!active) return;
                setState({ url: null, blob: null, loading: false, error: error.message || 'Erro ao carregar o arquivo.' });
            });

        return () => {
            active = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, size, enabled]);

    return state;
};
